import { useEffect, useState } from "react";
import { useCompanyViewModel } from "../../viewmodels/company/useCompanyViewModel";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useNavigate } from "react-router-dom";

const CompanyList = () => {
  const navigate = useNavigate();

  const {
    companies,
    loading,
    pageNumber,
    setPageNumber,
    deleteCompany,
    setSearch,       
  } = useCompanyViewModel();

  const [searchInput, setSearchInput] = useState("");

  // ============================================
  //  DEBOUNCE SEARCH
  // ============================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);      // reset page
      setSearch(searchInput); // trigger API
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = (id: string) => {
    const confirm = window.confirm(
      "Are you sure you want to delete this company?"
    );

    if (confirm) {
      deleteCompany(id);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">

        {/* BREADCRUMB */}
        <div className="text-sm text-gray-500 mb-3">
          Dashboard / Companies
        </div>

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
          <h2 className="text-xl font-semibold">Company Management</h2>

          <button
            onClick={() => navigate("/companies/create")}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
          >
            + Create Company
          </button>
        </div>

        {/*  SEARCH */}
        <div className="mb-4">
          <input
            placeholder="Search company..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full md:w-80 border px-3 py-2 rounded-lg"
          />
        </div>

       {/* TABLE */}
        <div className="bg-white rounded-xl shadow border border-slate-300 overflow-hidden">

          <div className="overflow-x-auto">

            <table className="min-w-[800px] w-full text-sm text-left border-collapse">

              <thead className="bg-gray-100 sticky top-0 z-10">

                <tr className="text-left">

                  <th className="p-3 font-medium border border-slate-300">
                    Name
                  </th>

                  <th className="p-3 font-medium border border-slate-300">
                    Email
                  </th>

                  <th className="p-3 font-medium border border-slate-300">
                    Phone
                  </th>

                  <th className="p-3 font-medium border border-slate-300 text-center">
                    Actions
                  </th>

                </tr>

              </thead>

              <tbody>

                {loading ? (

                  <tr>

                    <td
                      colSpan={4}
                      className="p-4 text-center border border-slate-300"
                    >
                      Loading...
                    </td>

                  </tr>

                ) : companies.length === 0 ? (

                  <tr>

                    <td
                      colSpan={4}
                      className="p-4 text-center text-gray-500 border border-slate-300"
                    >
                      No companies found
                    </td>

                  </tr>

                ) : (

                  companies.map((c) => (

                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 transition"
                    >

                      {/* NAME */}
                      <td className="p-3 font-medium border border-slate-300">
                        {c.name}
                      </td>

                      {/* EMAIL */}
                      <td className="p-3 border border-slate-300">
                        {c.contactEmail || "-"}
                      </td>

                      {/* PHONE */}
                      <td className="p-3 border border-slate-300">
                        {c.contactPhone || "-"}
                      </td>

                      {/* ACTIONS */}
                      <td className="p-3 border border-slate-300">

                        <div className="flex gap-2 justify-center">

                          <button
                            onClick={() =>
                              navigate(
                                `/companies/edit/${c.id}`
                              )
                            }
                            className="px-3 py-1 text-xs bg-yellow-500 text-white rounded"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() =>
                              handleDelete(c.id)
                            }
                            className="px-3 py-1 text-xs bg-red-600 text-white rounded"
                          >
                            Delete
                          </button>

                        </div>

                      </td>

                    </tr>

                  ))

                )}

              </tbody>

            </table>

          </div>

        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <button
            disabled={pageNumber === 1}
            onClick={() => setPageNumber(pageNumber - 1)}
          >
            Prev
          </button>

          <span className="text-sm">Page {pageNumber}</span>

          <button
            disabled={companies.length === 0}
            onClick={() => setPageNumber(pageNumber + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyList;